<template>
  <div class="scene-view">
    <div
      id="engineContainer"
      class="engine-container"
      ref="engineContainer"
      :style="`visibility:${canvasVisible ? 'visible' : 'hidden'}`" />
    <EngineLoading
      v-if="showpercentage"
      :percentage="percentage" />
  </div>
</template>

<script>
import { Loading } from 'element-ui'
import EngineLoading from '@/components/Loading.vue'

import { isTemplateVersionConsistentWithEngine } from '@/utils/generalMethods'

// 需要设置坐标系和基点的数据类型
const SETCRS_DATA_TYPE = [0, 11, 15]

export default {
  data() {
    return {
      percentage: 0, // 进度条数值
      showpercentage: true, // 是否显示进度条
      canvasVisible: false,

      isStartLoadDataSet: false
    }
  },
  props: {
    onUIThemeChange: Function,
    modelClick: Function,
    vectorClick: Function,
    loadMainScene: Function,
    onMainSceLoaded: Function,
    onCadLoadFinish: Function,
    handleElemClick: Function,
    handleCadElemClick: Function,
    togglePanelVisible: Function
  },
  components: {
    EngineLoading
  },
  mounted() {
    this.addEventListener()
  },
  beforeDestroy() {
    this.loading && this.loading.close()

    this.unloadAllMainSce()
    this.removeEventListener()
    this.showpercentage = false
  },
  methods: {
    addEventListener() {
      document.addEventListener('RESystemEngineCreated', this.onCreated)
      document.addEventListener('REDataSetLoadProgress', this.onProgress)
      document.addEventListener('REDataSetLoadFinish', this.onLoadFinish)
      document.addEventListener('RESystemUIEvent', this.onUIEvent)
      document.addEventListener('RESystemSelElement', this.onSelElement)
      document.addEventListener('RESystemSelShpElement', this.onSelShpElement)
      document.addEventListener('RECADSelElement', this.onRECADSelElement)
      document.addEventListener('REDataSetLoadPanFinish', this.onPanFinish)
      document.addEventListener('REPanLoadSingleFinish', this.onPanImgFinish)
      document.addEventListener('RECADLoadFinish', this.onCADFinish)
      document.addEventListener('mousedown', this.onMouseDown)

      window.addEventListener('beforeunload', this.onBeforeUnload)
      window.addEventListener('resize', this.onResize)
    },
    removeEventListener() {
      document.removeEventListener('RESystemEngineCreated', this.onCreated)
      document.removeEventListener('REDataSetLoadProgress', this.onProgress)
      document.removeEventListener('REDataSetLoadFinish', this.onLoadFinish)
      document.removeEventListener('RESystemUIEvent', this.onUIEvent)
      document.removeEventListener('RESystemSelElement', this.onSelElement)
      // prettier-ignore
      document.removeEventListener('RESystemSelShpElement', this.onSelShpElement)
      document.removeEventListener('RECADSelElement', this.onRECADSelElement)
      document.removeEventListener('REDataSetLoadPanFinish', this.onPanFinish)
      document.removeEventListener('REPanLoadSingleFinish', this.onPanImgFinish)
      document.removeEventListener('RECADLoadFinish', this.onCADFinish)
      document.removeEventListener('mousedown', this.onMouseDown)
    },
    initEngine() {
      window.BlackHole3D = window.BlackHole3D ? window.BlackHole3D : {}
      window.BlackHole3D.canvas = document.getElementById('engineCanvas')

      if (typeof window.BlackHole3D.RealBIMWeb === 'undefined') {
        window.BlackHole3D = window.CreateBlackHoleWebSDK(window.BlackHole3D)
      }
    },
    onBeforeUnload() {
      window.BlackHole3D.releaseEngine()
    },
    onResize() {
      this.setCanvasSize()
    },
    onMouseDown(e) {
      // 按下鼠标中键时，禁用滚动
      if (e.button === 1) {
        e.preventDefault()
      }
    },
    onCreated() {
      const timer = setInterval(() => {
        if (this.isEnginePreLoaded()) {
          clearInterval(timer)
          this.copyCanvas()

          this.loadMainScene()
        }
      }, 500)
    },
    async loadDataSet(dataType, modelInfo) {
      this.isStartLoadDataSet = true

      const isVersionConsist = await isTemplateVersionConsistentWithEngine()
      const BlackHole3D = window.BlackHole3D
      if (!isVersionConsist) {
        const dataSetList = [
          {
            dataSetId: 'res_skybox',
            resourcesAddress: window.skyBoxUrl
          }
        ]
        BlackHole3D.Model.loadDataSet(dataSetList)

        BlackHole3D.Common.setMaxResMemMB(2000)
        BlackHole3D.Common.setExpectMaxInstMemMB(1900)
        BlackHole3D.Common.setExpectMaxInstDrawFaceNum(10000000)
        BlackHole3D.Common.setPageLoadLev(2)
        this.$message.error('请升级SDK版本至3.2.0.3247之后的版本')
        return
      }

      if (dataType === 'Cad') {
        this.loadCad(modelInfo)
        return
      }
      if (dataType === 'Panorama') {
        BlackHole3D.Graphics.setSysUIPanelVisible(true)
        BlackHole3D.Graphics.setGeoCoordVisible(true)
        BlackHole3D.Graphics.setViewCubeVisible(true)
        this.loadPanSce(modelInfo)
        return
      }
      this.loadModelSce(modelInfo)
    },
    loadModelSce(modelInfo) {
      window.BlackHole3D.Graphics.setSysUIPanelVisible(true)
      window.BlackHole3D.Graphics.setGeoCoordVisible(true)
      window.BlackHole3D.Graphics.setViewCubeVisible(true)

      let dataSetList = []
      if (!modelInfo.length) {
        let obj = {
          dataSetId: 'res_skybox',
          resourcesAddress: window.skyBoxUrl
        }
        dataSetList.push(obj)
      } else {
        modelInfo.forEach(item => {
          let obj = {
            dataSetId: item.resId,
            resourcesAddress: this.getResourcesAddress(item),
            dataSetCRS: this.getDataSetCRS(item),
            engineOrigin: this.getEngineOrigin(item),
            dataSetCRSNorth: this.getDataSetCRSNorth(item),
            transInfo: this.getTransInfo(item),
            useTransInfo: true,
            dataSetSGContent: item.context ? item.context : ''
          }
          dataSetList.push(obj)
        })
      }
      window.BlackHole3D.Model.loadDataSet(dataSetList)

      window.BlackHole3D.Common.setMaxResMemMB(2000)
      window.BlackHole3D.Common.setExpectMaxInstMemMB(1900)
      window.BlackHole3D.Common.setExpectMaxInstDrawFaceNum(20000000)
      window.BlackHole3D.Common.setPageLoadLev(2)
    },
    loadPanSce(panInfo) {
      let dataSetList = []
      panInfo.forEach(item => {
        let obj = {
          dataSetId: item.resId,
          resourcesAddress: this.getResourcesAddress(item)
        }
        dataSetList.push(obj)
      })

      window.BlackHole3D.Panorama.loadPan(dataSetList)
    },
    loadCad(cadInfo) {
      this.showpercentage = false
      this.loading = Loading.service({
        text: '二维图纸加载中......',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      window.BlackHole3D.Graphics.setSysUIPanelVisible(false)
      window.BlackHole3D.Graphics.setGeoCoordVisible(false)
      window.BlackHole3D.Graphics.setViewCubeVisible(false)

      window.BlackHole3D.setViewMode(
        window.BlackHole3D.REVpTypeEm.CAD,
        window.BlackHole3D.REVpTypeEm.None,
        window.BlackHole3D.REVpRankEm.Single
      )

      const RECadUnitEm = window.BlackHole3D.RECadUnitEm
      const cadUnitMap = {
        Meter: RECadUnitEm.CAD_UNIT_Meter,
        Centimeter: RECadUnitEm.CAD_UNIT_Centimeter,
        Millimeter: RECadUnitEm.CAD_UNIT_Millimeter,
        Kilometer: RECadUnitEm.CAD_UNIT_Kilometer,
        Inch: RECadUnitEm.CAD_UNIT_Inch,
        Foot: RECadUnitEm.CAD_UNIT_Foot,
        Mile: RECadUnitEm.CAD_UNIT_Mile
      }
      const cadUnit = cadUnitMap[cadInfo.unit]
      window.BlackHole3D.CAD.loadCAD(cadInfo.resourcesAddress, cadUnit, 100)
      this.canvasVisible = true
    },
    async loadBIMCAD(cadInfo, canvasViewMode) {
      const isVersionConsist = await isTemplateVersionConsistentWithEngine()
      if (!isVersionConsist) {
        this.$message.error('请升级SDK版本至3.2.0.3247之后的版本')
        return
      }

      this.loading = Loading.service({
        text: '二维图纸加载中......',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      const BlackHole3D = window.BlackHole3D
      BlackHole3D.Graphics.setSysUIPanelVisible(false)
      BlackHole3D.Graphics.setGeoCoordVisible(false)
      BlackHole3D.Graphics.setViewCubeVisible(false)

      const cadUnit = BlackHole3D.RECadUnitEm.CAD_UNIT_Meter
      BlackHole3D.CAD.loadCAD(cadInfo.resourcesAddress, cadUnit, 1.0)
      if (canvasViewMode === 'bimCad') {
        BlackHole3D.setViewMode(
          BlackHole3D.REVpTypeEm.BIM,
          BlackHole3D.REVpTypeEm.CAD,
          BlackHole3D.REVpRankEm.LR
        )
      }
    },
    onPanFinish(eventInfo) {
      let detail = eventInfo.detail
      const BlackHole3D = window.BlackHole3D
      const Panorama = BlackHole3D.Panorama
      let panSceNames = Panorama.getAllDataSetNames()
      if (detail.succeed) {
        let pandata = Panorama.getElemInfo(panSceNames[0])
        BlackHole3D.setViewMode(
          BlackHole3D.REVpTypeEm.Panorama,
          BlackHole3D.REVpTypeEm.None,
          BlackHole3D.REVpRankEm.Single
        )
        let camLoc = new window.BlackHole3D.RECamLoc()
        camLoc.camRotate = [0, 0, 0, 1]
        Panorama.setCamLocateTo(window.BlackHole3D.RECamDirEm.CAM_DIR_LEFT, 0)
        Panorama.loadPanPic(pandata[0].elemId, 0)

        BlackHole3D.Graphics.setGeoCoordVisible(false)
        BlackHole3D.Graphics.setViewCubeVisible(false)
        BlackHole3D.Graphics.setSysUIPanelVisible(false)
      } else {
        console.log('360全景加载失败！')
      }
    },
    onPanImgFinish(eventInfo) {
      let detail = eventInfo.detail
      if (detail.succeed) {
        this.canvasVisible = true
        this.showpercentage = false
        if (this.onMainSceLoaded) {
          this.onMainSceLoaded()
        }
        console.log('图片设置成功！！！！！')
      } else {
        console.log('图片设置失败！！！！！')
      }
    },
    onCADFinish() {
      this.loading && this.loading.close()
      this.showpercentage = false

      this.onCadLoadFinish()
    },
    getResourcesAddress(dataSetInfo) {
      let resourcesAddress = ''
      if (window.isMinIO) {
        resourcesAddress = `${dataSetInfo.resourcesAddress}${dataSetInfo.resId}`
      } else {
        resourcesAddress = dataSetInfo.resourcesAddress
      }
      return resourcesAddress
    },
    getDataSetCRS(dataSetInfo) {
      if (!SETCRS_DATA_TYPE.includes(dataSetInfo.dataSetType)) return null

      let crsConfig = dataSetInfo.coordinatesConfig
      if (crsConfig.coordinatesType === 'None') return null

      if (crsConfig.coordinatesType === 'CalibrationPoint') {
        let crsPoint = crsConfig.coordinatesPoint
        let crs = `ENU:${crsPoint.latitude},${crsPoint.longitude}`
        return crs
      } else {
        return crsConfig.coordinates
      }
    },
    getEngineOrigin(dataSetInfo) {
      if (!SETCRS_DATA_TYPE.includes(dataSetInfo.dataSetType)) return null

      let engineOrigin = []
      let crsConfig = dataSetInfo.coordinatesConfig

      let origin = crsConfig.basePoint
      let originArray = []
      if (origin) {
        originArray = origin.split(',').map(Number)
      }

      if (crsConfig.coordinatesType === 'CalibrationPoint') {
        let crsPoint = crsConfig.coordinatesPoint
        let point = crsPoint.coordinates
        let pointArray = []
        if (point) {
          pointArray = point.split(',').map(Number)
        } else {
          pointArray = [0, 0, 0]
        }

        if (originArray.length) {
          originArray.forEach((item, index) => {
            let result = item - pointArray[index]
            engineOrigin.push(result)
          })
        } else {
          engineOrigin = pointArray
        }
      } else {
        engineOrigin = originArray
      }

      if (engineOrigin.length) {
        return engineOrigin
      } else {
        return null
      }
    },
    getDataSetCRSNorth(dataSetInfo) {
      if (!SETCRS_DATA_TYPE.includes(dataSetInfo.dataSetType)) return null
      let crsConfig = dataSetInfo.coordinatesConfig
      if (crsConfig.northAngle) {
        return Number(crsConfig.northAngle)
      } else {
        return null
      }
    },
    getTransInfo(dataSetInfo) {
      let sacle = dataSetInfo.scale.split(' ').map(Number)
      let rotate = dataSetInfo.rotate.split(' ').map(Number)
      let translation = dataSetInfo.translation.split(' ').map(Number)
      let transInfo = [sacle, rotate, translation]
      return transInfo
    },
    onLoadFinish() {
      if (!this.isStartLoadDataSet) return

      this.canvasVisible = true
      this.showpercentage = false

      if (this.onMainSceLoaded) {
        this.onMainSceLoaded()
      }

      // 解决模型加载完成后，键盘WASD不能控制引擎相机移动的问题
      setTimeout(() => {
        window.BlackHole3D.canvas.focus()
      }, 500)
    },
    onProgress(eventInfo) {
      if (!this.isStartLoadDataSet) return

      let percentage = eventInfo.detail.progress
      if (percentage < 10) {
        this.fromZeroFlag = true
      }
      if (percentage === 12 && this.fromZeroFlag) {
        percentage = 10
      }

      this.percentage = percentage
    },
    onUIEvent(eventInfo) {
      let detail = eventInfo.detail
      let btnName = detail.btnname
      if (btnName.includes('Measure')) {
        window.BlackHole3D.BIM.delAllSelElems()
      }

      if (btnName.includes('UIClr_Style')) {
        if (this.onUIThemeChange) {
          this.onUIThemeChange(btnName)
        }
      }

      const toggleActiveState = (btnName, btnState) => {
        window.BlackHole3D.Graphics.setBtnActiveState(btnName, btnState)
        this.togglePanelVisible(btnName)
      }

      const btnNames = ['Coplane', 'Catalog_Tree', 'Attribute', 'Texture']
      let find = btnNames.find(el => btnName.includes(el))
      if (find) {
        const btnState = detail.btnstate
        const nextState = btnState === 0 ? 1 : 0
        toggleActiveState(btnName, nextState)
      }
    },
    onSelElement() {
      let eleInfo = window.BlackHole3D.Probe.getCurCombProbeRet()
      // 处理纹理动画编辑的逻辑
      let editFlag = window.BlackHole3D.Pipe.getContPipeMode()
      if (editFlag) {
        this.handleElemClick(eleInfo.elemId)
        return
      }

      window.BlackHole3D.BIM.resetSelElemsAttr()
      if (eleInfo.elemType === 'BIMElem') {
        let blendAttr = new window.BlackHole3D.RESelElemsBlendAttr()
        blendAttr.elemClr = new window.BlackHole3D.REColor(254, 64, 7, 180)
        blendAttr.clrWeight = 255
        blendAttr.alphaWeight = 255
        blendAttr.probeMask = 1
        blendAttr.attrValid = true
        window.BlackHole3D.BIM.setSelElemsBlendAttr(blendAttr)
      } else {
        let blendAttr = new window.BlackHole3D.RESelElemsBlendAttr()
        let color = new window.BlackHole3D.REColor(254, 64, 7, 0.8 * 255)
        blendAttr.elemClr = color
        blendAttr.clrWeight = 0.3 * 255
        blendAttr.alphaWeight = 255
        blendAttr.probeMask = 1
        blendAttr.attrValid = true
        window.BlackHole3D.BIM.setSelElemsBlendAttr(blendAttr)
      }

      if (this.modelClick) {
        this.modelClick(eleInfo)
      }
    },
    onSelShpElement(event) {
      if (event.detail.button !== 0) return

      let vectorInfo = window.BlackHole3D.Probe.getCurShpProbeRet()
      let elemId = vectorInfo.elemId
      if (elemId) {
        this.vectorClick(elemId)
      }
    },
    onRECADSelElement(eventInfo) {
      let cadElemId = eventInfo.detail.elemid
      this.heightCADElement(cadElemId)
      this.handleCadElemClick(cadElemId)
    },
    heightCADElement(cadElemId) {
      window.BlackHole3D.CAD.setCamLocateToElem(cadElemId, 1.0)
      window.BlackHole3D.CAD.selElem(cadElemId)
    },
    highLightDatasets(datasetIds) {
      window.BlackHole3D.BIM.delAllSelElems()
      datasetIds.forEach(datasetId => {
        window.BlackHole3D.BIM.addToSelDataSet(datasetId)
        window.BlackHole3D.Camera.setCamLocateToDataSet(datasetId, 1.0)

        this.setSelElemsBlendAttr()
      })
    },
    showDataset(datasetType, datasetId, focusFlag) {
      if (datasetType !== 0) {
        window.BlackHole3D.Grid.setValidState(datasetId, true)
      } else {
        window.BlackHole3D.BIM.resetElemAttr(datasetId, [])
      }
      if (focusFlag) {
        window.BlackHole3D.Camera.setCamLocateToDataSet(datasetId, 1.0)
      }
    },
    hiddenDataset(datasetType, datasetId) {
      if (datasetType !== 0) {
        window.BlackHole3D.Grid.setValidState(datasetId, false)
      } else {
        window.BlackHole3D.BIM.setElemAlpha(datasetId, [], 0)
      }
    },
    highLightElement(datasetId, childNodeIds, isRootnode) {
      if (isRootnode) {
        window.BlackHole3D.BIM.delAllSelElems()
        window.BlackHole3D.BIM.addToSelDataSet(datasetId)
        window.BlackHole3D.Camera.setCamLocateToDataSet(datasetId, 1.0)

        this.setSelElemsBlendAttr()
        return
      }

      window.BlackHole3D.BIM.delAllSelElems()
      window.BlackHole3D.BIM.addToSelElems(datasetId, childNodeIds)
      window.BlackHole3D.Camera.setCamLocateToElem(
        [
          {
            dataSetId: datasetId,
            elemIdList: childNodeIds
          }
        ],
        1.0
      )
      this.setSelElemsBlendAttr()
    },
    hiddenElement(datasetId, childNodeIds) {
      window.BlackHole3D.BIM.delAllSelElems()
      window.BlackHole3D.BIM.setElemAlpha(datasetId, childNodeIds, 0)
    },
    showElement(datasetId, childNodeIds) {
      window.BlackHole3D.BIM.delAllSelElems()
      window.BlackHole3D.BIM.resetElemAttr(datasetId, childNodeIds)
    },
    showRoom(datasetId, topHeight, bottomHeight, childNodeIds) {
      const BlackHole3D = window.BlackHole3D
      const Clip = BlackHole3D.Clip
      let clipPlanesClr = new BlackHole3D.REColor(254, 255, 255, 0)
      BlackHole3D.BIM.setClipPlanesContourLineClr(clipPlanesClr)
      Clip.setClipSpecifyHeight(datasetId, topHeight, bottomHeight, true)

      // 重置所有构件颜色，防止切换房间时，上次选中的房间颜色依旧存在
      BlackHole3D.BIM.resetElemAttr(datasetId, [])
      let locIDList = [{ dataSetId: datasetId, elemIdList: childNodeIds }]
      const CAM_DIR_TOP = BlackHole3D.RECamDirEm.CAM_DIR_TOP
      BlackHole3D.Camera.setCamLocateToElem(locIDList, 1.0, CAM_DIR_TOP)

      let elemAttr = new BlackHole3D.REElemAttr()
      let elemClr = new BlackHole3D.REColor(254, 64, 7, 255)
      elemAttr.dataSetId = datasetId
      elemAttr.elemIdList = childNodeIds
      elemAttr.elemClr = elemClr
      BlackHole3D.BIM.setElemAttr(elemAttr)
      BlackHole3D.BIM.setElemDepthBias(datasetId, childNodeIds, -0.00002)
    },
    showRoomByElemId(datasetId, childNodeIds) {
      window.BlackHole3D.BIM.delAllSelElems()
      let elemAttr = new window.BlackHole3D.REElemAttr()
      let elemClr = new window.BlackHole3D.REColor(255, 0, 0, 255)
      elemAttr.dataSetId = datasetId
      elemAttr.elemIdList = childNodeIds
      elemAttr.elemClr = elemClr
      window.BlackHole3D.BIM.setElemAttr(elemAttr)
      let locIDList = [{ dataSetId: datasetId, elemIdList: childNodeIds }]
      window.BlackHole3D.Camera.setCamLocateToElem(locIDList, 2.0)
    },
    resetViewMode() {
      const BlackHole3D = window.BlackHole3D
      const BIM = BlackHole3D.REVpTypeEm.BIM
      const None = BlackHole3D.REVpTypeEm.None
      const Single = BlackHole3D.REVpRankEm.Single
      BlackHole3D.setViewMode(BIM, None, Single)

      BlackHole3D.Graphics.setSysUIPanelVisible(true)
      BlackHole3D.Graphics.setGeoCoordVisible(true)
      BlackHole3D.Graphics.setViewCubeVisible(true)
    },
    toggleCanvasViewMode(canvasViewMode) {
      const BlackHole3D = window.BlackHole3D
      if (canvasViewMode === 'bimCad') {
        BlackHole3D.setViewMode(
          BlackHole3D.REVpTypeEm.BIM,
          BlackHole3D.REVpTypeEm.CAD,
          BlackHole3D.REVpRankEm.LR
        )
      } else {
        BlackHole3D.setViewMode(
          BlackHole3D.REVpTypeEm.CAD,
          BlackHole3D.REVpTypeEm.None,
          BlackHole3D.REVpRankEm.Single
        )
      }
    },
    addCustomBtn(uiID, stateParList, activeStateId) {
      let btnInfo = new window.BlackHole3D.REUIBtnInfo()
      btnInfo.uiID = uiID
      btnInfo.activeStateId = activeStateId

      let tempStateParList = stateParList.map(item => {
        let statePar = new window.BlackHole3D.REUIBtnStateInfo()
        statePar.hintText = item.hintText
        statePar.texPath = item.texPath
        return statePar
      })

      btnInfo.stateParList = tempStateParList
      window.BlackHole3D.Graphics.createSysPanelBtn(btnInfo)
    },
    setSelElemsBlendAttr() {
      const BlackHole3D = window.BlackHole3D
      let blendAttr = BlackHole3D.BIM.getSelElemsBlendAttr()
      blendAttr.elemClr = new BlackHole3D.REColor(255, 0, 0, 200)
      blendAttr.clrWeight = 128
      blendAttr.alphaWeight = 200
      BlackHole3D.BIM.setSelElemsBlendAttr(blendAttr)
    },
    unloadAllMainSce() {
      window.BlackHole3D.CAD.unloadCAD()
      window.BlackHole3D.Model.unloadAllDataSet()
      window.BlackHole3D.Panorama.unloadDataSet([])
    },
    setCanvasSize() {
      const domRef = this.$refs.engineContainer
      if (domRef) {
        window.BlackHole3D.m_re_em_window_width = domRef.clientWidth
        window.BlackHole3D.m_re_em_window_height = domRef.clientHeight
      }
    },
    copyCanvas() {
      const engineContainer = this.$refs.engineContainer
      const engineCanvas = document.getElementById('engineCanvas')
      engineContainer.append(engineCanvas)

      this.setCanvasSize()
    },
    isEnginePreLoaded() {
      let isPreLoaded = false
      const engineCanvas = document.getElementById('engineCanvas')
      const classList = engineCanvas.classList
      if (classList.contains('is-loaded')) {
        isPreLoaded = true
      }
      return isPreLoaded
    },
    isEngineCanvasExist() {
      let isExist = false
      const engineCanvas = document.getElementById('engineCanvas')
      if (engineCanvas) {
        isExist = true
      } else {
        isExist = false
      }
      return isExist
    },
    createEngineCanvas() {
      const engingCanvasDom = document.createElement('canvas')
      engingCanvasDom.id = 'engineCanvas'
      engingCanvasDom.classList.add('engine-canvas-box')
      // prettier-ignore
      engingCanvasDom.oncontextmenu = e => { e.preventDefault() }
      engingCanvasDom.tabIndex = '1'

      const engineContainer = document.getElementById('engineContainer')
      engineContainer.append(engingCanvasDom)
    }
  }
}
</script>

<style lang="less" scoped>
.scene-view {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--eng-loading-bgColor);

  .engine-container {
    height: 100%;
    :deep(.engine-canvas-box) {
      outline: none;
      &:focus {
        outline: none;
      }
    }
  }
}
</style>
